import { createContext, useContext, useState, useCallback } from 'react';

const CartContext = createContext(null);

export function CartProvider({ children }) {
    const [items, setItems] = useState([]);
    const [discount, setDiscount] = useState(0);
    const [tip, setTip] = useState(0);
    const [taxRate, setTaxRate] = useState(0.08); // Default 8%, overridden by store settings

    const addItem = useCallback((product) => {
        setItems(prev => {
            const existing = prev.find(item => item.product_id === product.id);
            if (existing) {
                return prev.map(item =>
                    item.product_id === product.id
                        ? { ...item, quantity: item.quantity + 1, subtotal: (item.quantity + 1) * item.unit_price }
                        : item
                );
            }
            return [...prev, {
                product_id: product.id,
                product_name: product.name,
                quantity: 1,
                unit_price: product.price,
                subtotal: product.price
            }];
        });
    }, []);

    const removeItem = useCallback((productId) => {
        setItems(prev => prev.filter(item => item.product_id !== productId));
    }, []);

    const updateQuantity = useCallback((productId, quantity) => {
        if (quantity <= 0) {
            removeItem(productId);
            return;
        }
        setItems(prev => prev.map(item =>
            item.product_id === productId
                ? { ...item, quantity, subtotal: quantity * item.unit_price }
                : item
        ));
    }, [removeItem]);

    const clearCart = useCallback(() => {
        setItems([]);
        setDiscount(0);
        setTip(0);
    }, []);

    const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
    const stateTaxRate = taxRate / 2;
    const countyTaxRate = taxRate / 2;
    const stateTax = subtotal * stateTaxRate;
    const countyTax = subtotal * countyTaxRate;
    const tax = subtotal * taxRate;
    const total = subtotal + tax + tip - discount;
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    const value = {
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        discount,
        setDiscount,
        tip,
        setTip,
        taxRate,
        setTaxRate,
        subtotal,
        stateTax,
        countyTax,
        tax,
        total,
        itemCount,
        stateTaxRate,
        countyTaxRate
    };

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}
